import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import type {SxProps} from '@mui/material';
import {Autocomplete} from '@mui/material';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import {lighten} from '@mui/system/colorManipulator';
import {type FC, type ReactNode, type SyntheticEvent, useEffect, useState} from 'react';
import {primaryColor, secondaryColor} from '@/theme/theme';

// TYPES
type Props = {
  value?: string;
  options?: Array<string>;
  required?: boolean;
  startAdornment?: ReactNode;
  editable: boolean;
  onValueChange: (optionId?: string) => void;
};

const AutocompleteEditableOnHover: FC<Props> = ({value, options = [], onValueChange, editable}) => {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);

  const [newValue, setNewValue] = useState<string | undefined>(value);

  const handleMouseEnter = () => editable && setHover(true);
  const handleMouseLeave = () => editable && setHover(false);

  // Sync value externo → interno
  useEffect(() => {
    if (value) {
      const selectedOption = options.find((opt) => opt === value);
      setNewValue(selectedOption ?? undefined);
    } else {
      setNewValue(undefined);
    }
  }, [value, options]);

  const handleChange = (_e: SyntheticEvent, newVal: string | null) => {
    const normalized = newVal ?? undefined;
    setNewValue(normalized);
    onValueChange(normalized);
  };

  const handleClose = () => {
    setEditing(false);
    setHover(false);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={styles(hover, editing).container}
    >
      <Autocomplete
        disabled={!editable}
        value={newValue ?? null}
        onChange={handleChange}
        options={options}
        getOptionLabel={(option) => option ?? ''}
        isOptionEqualToValue={(option, val) => option === val}
        onOpen={() => setEditing(true)}
        onClose={handleClose}
        size="small"
        renderInput={(params) => <TextField {...params} variant="outlined" />}
        sx={styles(hover, editing).autocomplete}
        slotProps={{
          popper: {
            placement: 'bottom-start',
          },
          paper: {
            sx: {
              maxHeight: 300,
              mt: 0.5,
            },
          },
        }}
      />

      <EditOutlinedIcon color="primary" sx={styles(hover, editing).icon} />
    </Stack>
  );
};

const styles = (hover: boolean, editing: boolean): Record<string, SxProps> => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: hover ? '1px solid #d3d3d3' : '1px solid transparent',
    backgroundColor: hover ? lighten(secondaryColor, 0.9) : 'white',
    transition: 'all 500ms ease-in-out',
    borderRadius: 1,
    width: '100%',
    pr: 0.5,
  },
  autocomplete: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      padding: 0,
      height: '34px',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
      },
      ...(editing && {
        borderBottom: `2px solid ${primaryColor}`,
      }),
      '& input': {
        padding: '8px 4px !important',
        fontWeight: 900,
        fontSize: 'inherit',
      },
      '& .MuiAutocomplete-endAdornment': {
        opacity: editing ? 1 : 0,
        transition: 'opacity 200ms ease-in-out',
      },
    },
    '& .MuiOutlinedInput-root:focus-within': {
      ...(editing && {
        borderBottom: `2px solid ${primaryColor}`,
      }),
    },
    '& .MuiAutocomplete-listbox': {
      padding: '8px 0',
      '& .MuiAutocomplete-option': {
        padding: '10px 16px',
        fontSize: 'inherit',

        '&[aria-selected="true"]': {
          backgroundColor: lighten(primaryColor, 0.8),
          fontWeight: 600,
        },
        '&:hover': {
          backgroundColor: lighten(secondaryColor, 0.85),
        },
      },
    },
  },
  icon: {
    mr: 1,
    opacity: hover ? 1 : 0,
    transition: 'opacity 500ms ease-in-out',
    cursor: 'pointer',
  },
});

export default AutocompleteEditableOnHover;
